import React from 'react'
import { Button, Icon, Text } from '@adc/ui-core'
import { user, flows } from '@adc/blueprint'
import type {
  BuyingCriteriaLocation,
  BuyingCriteriaData,
  BuyingCriteriaActions,
} from '@adc/blueprint'
import classnames from 'classnames/bind'
import {
  buyingPreferenceFlowDefinition,
  BuyingPreferenceStepId,
  STEP_DESCRIPTIONS,
} from './buyingPreferenceFlow'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const { useFlow } = flows.hooks

export interface ProgressiveCaptureContentProps {
  data: BuyingCriteriaData
  actions: BuyingCriteriaActions
  /** Called when user confirms their selection */
  onConfirm: () => void
  /** Called when user skips */
  onSkip: () => void
  /** Called when user clicks "Answer More" in success state */
  onAnswerMore: () => void
  /** Called when user clicks "Done for Now" in success state */
  onDone: () => void
  /** Number of confirmed attributes (for success state message) */
  completedCount?: number
  /** Force the flow to start at a specific step (for testing/stories) */
  startAtStep?: BuyingPreferenceStepId
  /** First-ever nudge: force location step even if already filled */
  isPristine?: boolean
  /** Automation ID prefix */
  elmId?: string
}

/**
 * ProgressiveCaptureContent — renders the buying preference capture flow.
 *
 * Uses `useFlow` to manage step progression. Each step renders one preference
 * question. Steps with existing data are auto-skipped.
 *
 * Two states:
 * 1. Current step: renders the step's component + Confirm/Skip
 * 2. Success: after confirm, shows "Buying Criteria Updated" + Answer More/Done
 */
const ProgressiveCaptureContent: React.FC<ProgressiveCaptureContentProps> = ({
  data,
  actions,
  onConfirm,
  onSkip,
  onAnswerMore,
  onDone,
  completedCount = 0,
  startAtStep,
  isPristine = false,
  elmId = 'progressive-capture',
}) => {
  const [showSuccess, setShowSuccess] = React.useState(false)
  const [isConfirming, setIsConfirming] = React.useState(false)

  const flowContext = React.useMemo(
    () => ({
      filterViewModels: data.filterData.filterViewModels,
      updateFilter: (groupName: string, key: string, value: any) =>
        actions.updateFilter(groupName, key, value),
      updateLocations: (locs: BuyingCriteriaLocation[]) =>
        actions.updateLocations(locs),
      isPristine,
    }),
    [data.filterData.filterViewModels, actions, isPristine]
  )

  const flowDefinition = React.useMemo(() => {
    if (!startAtStep) return buyingPreferenceFlowDefinition
    const idx = buyingPreferenceFlowDefinition.findIndex(
      s => s.id === startAtStep
    )
    if (idx <= 0) return buyingPreferenceFlowDefinition
    return buyingPreferenceFlowDefinition.slice(idx)
  }, [startAtStep])

  const { data: flowData, actions: flowActions } = useFlow({
    definition: flowDefinition,
    context: flowContext,
    initialData: data.criteria,
  })

  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      await actions.saveImmediate()
      onConfirm()
      setShowSuccess(true)
    } finally {
      setIsConfirming(false)
    }
  }

  const handleSkip = () => {
    const groupDef =
      user.constants.NUDGE_GROUP_DEFINITIONS[flowData.currentStepObject.id]
    if (groupDef) {
      actions.discardGroup(groupDef.name)
    }
    onSkip()
  }

  if (showSuccess) {
    return (
      <div className={cx('success')} data-elm-id={`${elmId}-success`}>
        <Icon
          elmId={`${elmId}-success-icon`}
          name='circle-check'
          iconStyle='solid'
          size='large'
          color='success'
        />
        <Text.Header level={4}>Buying Criteria Updated</Text.Header>
        <Text color='gray' size='small'>
          Nice, that helps us get smarter for you. We&apos;ll use this to
          surface better-fit deals and recommendations.
        </Text>
        {completedCount > 0 && (
          <div className={cx('progress-badge')}>
            You&apos;ve completed {completedCount} of 7 buying criteria
          </div>
        )}
        <div className={cx('success-actions')}>
          <Button
            role='primary'
            elmId={`${elmId}-answer-more`}
            onClick={onAnswerMore}
          >
            Answer More
          </Button>
          <Button role='secondary' elmId={`${elmId}-done`} onClick={onDone}>
            Done for Now
          </Button>
        </div>
      </div>
    )
  }

  const StepComponent = flowData.currentStepObject.component
  const isStepValid = !!flowData.currentStepObject.isNextStepEnabled?.({
    data: data.criteria,
    context: flowContext,
  })
  const stepId = flowData.currentStepObject.id as BuyingPreferenceStepId
  const description = STEP_DESCRIPTIONS[stepId]

  return (
    <div className={cx('root')} data-elm-id={`${elmId}-question`}>
      <div className={cx('header')}>
        <div className={cx('header-title')}>
          <Icon
            elmId={`${elmId}-header-icon`}
            name='comments'
            iconStyle='solid'
            color='info'
            style={{ fontSize: 18 }}
          />
          <Text
            as='span'
            size='large'
            weight='heavy'
            color='grayDark'
            style={{ fontWeight: 800 }}
          >
            Want better deals?
          </Text>
        </div>
        <p className={cx('header-subtitle')}>
          Answer a quick question and we&apos;ll improve your property matches
          and recommendations.
        </p>
      </div>

      <div className={cx('question-content')}>
        <Text
          as='span'
          size='large'
          weight='heavy'
          color='grayDark'
          style={{ fontWeight: 800 }}
        >
          {flowData.currentStepObject.label}
        </Text>
        {description && (
          <Text color='gray' size='small'>
            {description}
          </Text>
        )}
        <StepComponent
          data={data.criteria}
          setData={() => {}}
          progress={flowData.currentProgress}
          touched={flowData.currentStepTouched}
          setTouched={flowActions.setCurrentStepTouched}
          context={flowContext}
        />
      </div>

      <div className={cx('question-actions')}>
        <Button
          role='tertiary'
          elmId={`${elmId}-skip`}
          onClick={handleSkip}
          className={cx('skip-button')}
        >
          Skip for Now
        </Button>
        <Button
          role='primary'
          elmId={`${elmId}-confirm`}
          onClick={handleConfirm}
          className={cx('confirm-button')}
          loading={isConfirming}
          disabled={!flowData.currentStepTouched && !isStepValid}
        >
          Confirm
        </Button>
      </div>
    </div>
  )
}

export default ProgressiveCaptureContent
